import React, { useState } from "react";
import { Sparkles, Scissors, Plus, Pencil, Trash2, ListMusic, Search, Play, X, Upload, Image as ImageIcon, CheckCircle2 } from "lucide-react";
import { useLang } from "../../context/LanguageContext";
import { useCatalog } from "../../context/CatalogContext";
import { usePlayer } from "../../context/PlayerContext";
import { CATEGORIES, formatTime } from "../../mock";
import { Button } from "../../components/ui/button";
import BookCover from "../../components/BookCover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { useToast } from "../../hooks/use-toast";
import client, { uploadFile } from "../../api";

// The API reports failures as {"detail": "..."}. Surfacing it matters here:
// "Bunny Storage not configured" is actionable, "Upload failed" is not.
function uploadError(err) {
  const detail = err?.response?.data?.detail;
  if (detail) return detail;
  if (err?.message) return err.message;
  return "Unknown error";
}


const emptyBook = {
  title_en: "", author: "", narrator: "", category: "fiction",
  price: "9.99", language: "en", cover: "", sampleUrl: "", desc_en: "", status: "draft",
  series: "", seriesPosition: "",
};

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

const inputCls = "w-full h-10 px-3 rounded-lg bg-secondary border border-border text-sm outline-none focus:border-brand-500/60 focus:ring-2 focus:ring-brand-500/20 transition-all";

export default function AdminBooks() {
  const { t } = useLang();
  const { adminBooks: books, addBook, updateBook, deleteBook, addChapter, updateChapter, deleteChapter } = useCatalog();
  const { playTrack } = usePlayer();
  const { toast } = useToast();
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null); // book or emptyBook
  const [chaptersFor, setChaptersFor] = useState(null); // bookId
  const [coverUploading, setCoverUploading] = useState(0); // 0 = idle, else percent (use -1 as "start")
  const { refresh } = useCatalog();

  const filtered = books.filter(
    (b) => b.title_en.toLowerCase().includes(query.toLowerCase()) || b.author.toLowerCase().includes(query.toLowerCase())
  );

  const openNew = () => setEditing({ ...emptyBook, id: null });
  const openEdit = (b) => setEditing({ ...b });

  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!editing.title_en?.trim()) {
      toast({ title: "Give it a title first", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await updateBook(editing.id, editing);
        toast({ title: "Audiobook updated" });
      } else {
        await addBook(editing);
        toast({ title: "Audiobook created" });
      }
      setEditing(null);
    } catch (err) {
      // The publish guard answers 409 with what is missing, which is the whole
      // point of it. Throwing silently left the dialog open and said nothing.
      toast({ title: "Not saved", description: uploadError(err), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const [ratingsBusy, setRatingsBusy] = useState(false);

  // Gives a freshly imported title a believable aggregate so it does not sit
  // at "0 ratings", which readers take as unreviewed rather than new.
  const handleSeedRatings = async () => {
    if (!editing?.id) return;
    setRatingsBusy(true);
    try {
      const res = await client.post(`/admin/books/${editing.id}/ratings/seed`);
      setEditing((prev) => ({ ...prev, rating: res.data.rating, ratingCount: res.data.ratingCount }));
      await refresh();
      toast({
        title: "Ratings seeded",
        description: `${res.data.rating} stars from ${res.data.ratingCount} ratings.`,
      });
    } catch (err) {
      toast({ title: "Could not seed ratings", description: uploadError(err), variant: "destructive" });
    } finally {
      setRatingsBusy(false);
    }
  };

  const [sampleBusy, setSampleBusy] = useState(false);

  // Cuts the opening of chapter one server-side and stores it as the sample.
  const handleGenerateSample = async () => {
    if (!editing?.id) return;
    setSampleBusy(true);
    try {
      const res = await client.post(`/admin/books/${editing.id}/sample/generate`);
      const info = res.data?._sample;
      setEditing((prev) => ({ ...prev, samplePath: res.data.samplePath, sampleUrl: res.data.sampleUrl }));
      await refresh();
      toast({
        title: "Sample generated",
        description: info
          ? `${Math.round(info.seconds / 60)} min taken from the first chapter (${(info.bytes / 1048576).toFixed(1)} MB).`
          : undefined,
      });
    } catch (err) {
      toast({ title: "Could not generate the sample", description: uploadError(err), variant: "destructive" });
    } finally {
      setSampleBusy(false);
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editing?.id) return;
    setCoverUploading(1);
    try {
      const res = await uploadFile(`/admin/upload/cover/${editing.id}`, file, (p) => setCoverUploading(Math.max(1, p)));
      setEditing((prev) => ({ ...prev, cover: res.data.cover, coverPath: res.data.coverPath }));
      await refresh();
      toast({ title: "Cover uploaded to Bunny"});
    } catch (err) {
      toast({
        title: "Cover upload failed",
        description: uploadError(err),
        variant: "destructive",
      });
    } finally {
      setCoverUploading(0);
    }
  };

  // Existing names, so a second volume joins the same series instead of
  // creating "Harry Potter " with a trailing space.
  const seriesNames = React.useMemo(
    () => [...new Set(books.map((b) => b.series).filter(Boolean))].sort(),
    [books],
  );

  const chapterBook = books.find((b) => b.id === chaptersFor);

  const statusColor = (s) =>
    s === "published" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" :
    s === "draft" ? "bg-brand-500/15 text-brand-400 border-brand-500/30" :
    "bg-secondary text-muted-foreground border-border";

  return (
    <div className="animate-float-up">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">{t("admin_books")}</h1>
          <p className="text-sm text-muted-foreground">{books.length} {t("results")}</p>
        </div>
        <Button onClick={openNew} className="bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold rounded-full">
          <Plus className="w-4 h-4 mr-1.5" /> {t("new_book")}
        </Button>
      </div>

      <div className="relative mb-5 max-w-sm">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("search_placeholder")}
          className="w-full h-10 pl-10 pr-4 rounded-xl bg-secondary border border-border text-sm outline-none focus:border-brand-500/60" />
      </div>

      <div className="rounded-2xl border border-border bg-card/50 overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="border-b border-border text-left text-xs text-muted-foreground uppercase tracking-wide">
              <th className="px-4 py-3 font-medium">{t("title")}</th>
              <th className="px-4 py-3 font-medium">{t("category")}</th>
              <th className="px-4 py-3 font-medium">{t("chapters")}</th>
              <th className="px-4 py-3 font-medium">{t("price")}</th>
              <th className="px-4 py-3 font-medium">{t("status")}</th>
              <th className="px-4 py-3 font-medium text-right">{t("actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {filtered.map((b) => (
              <tr key={b.id} className="hover:bg-secondary/40 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <BookCover book={b} showBrand={false} className="w-9 h-[52px] rounded shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium truncate max-w-[220px]">{b.title_en}</p>
                      <p className="text-xs text-muted-foreground truncate">{b.author}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {CATEGORIES.find((c) => c.id === b.category)?.en}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{b.chapters.length}</td>
                <td className="px-4 py-3 text-muted-foreground">£{Number(b.price).toFixed(2)}</td>
                <td className="px-4 py-3">
                  <Badge variant="outline" className={statusColor(b.status)}>{b.status}</Badge>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => setChaptersFor(b.id)} title={t("manage_chapters")}
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-brand-400 hover:bg-secondary transition-colors">
                      <ListMusic className="w-4 h-4" />
                    </button>
                    <button onClick={() => openEdit(b)} className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => {
                        if (window.confirm(`Delete "${b.title_en}" and its ${b.chapters.length} chapters? Anyone who owns it loses access. This cannot be undone.`)) deleteBook(b.id);
                      }}
                      aria-label={`Delete ${b.title_en}`}
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-red-400 hover:bg-secondary transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Book editor dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing?.id ? t("edit") : t("new_book")}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid sm:grid-cols-2 gap-4 py-2">
              <Field label={t("title")}>
                <input className={inputCls} value={editing.title_en} onChange={(e) => setEditing({ ...editing, title_en: e.target.value })} />
              </Field>
              <Field label={t("author")}>
                <input className={inputCls} value={editing.author} onChange={(e) => setEditing({ ...editing, author: e.target.value })} />
              </Field>
              <Field label={t("narrator")}>
                <input className={inputCls} value={editing.narrator} onChange={(e) => setEditing({ ...editing, narrator: e.target.value })} />
              </Field>
              <Field label={t("category")}>
                <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                  <SelectTrigger className="h-10 bg-secondary border-border"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.en}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={`${t("price")} (£)`}>
                <input type="number" step="0.01" className={inputCls} value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} />
              </Field>
              <Field label={t("status")}>
                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger className="h-10 bg-secondary border-border"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">draft</SelectItem>
                    <SelectItem value="published">published</SelectItem>
                    <SelectItem value="unpublished">unpublished</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Ratings">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground">
                    {editing.rating ? `${editing.rating} · ${editing.ratingCount || 0} ratings` : "None yet"}
                  </span>
                  {editing.id ? (
                    <button
                      type="button"
                      onClick={handleSeedRatings}
                      disabled={ratingsBusy}
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-secondary border border-border text-xs font-medium hover:border-brand-500/50 transition-colors disabled:opacity-50"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      {ratingsBusy ? "Seeding…" : "Seed ratings"}
                    </button>
                  ) : null}
                </div>
              </Field>
              {/* Optional. Most books belong to no series, so this stays empty
                  and quiet; filling it in is what links the volumes together. */}
              <Field label="Series (optional)">
                <input
                  className={inputCls}
                  list="series-names"
                  value={editing.series || ""}
                  onChange={(e) => setEditing({ ...editing, series: e.target.value })}
                  placeholder="e.g. Harry Potter"
                />
                <datalist id="series-names">
                  {seriesNames.map((n) => <option key={n} value={n} />)}
                </datalist>
              </Field>
              <Field label="Number in the series">
                <input
                  type="number" min="1" step="1" className={inputCls}
                  value={editing.seriesPosition ?? ""}
                  disabled={!editing.series}
                  onChange={(e) => setEditing({ ...editing, seriesPosition: e.target.value })}
                  placeholder={editing.series ? "1" : "Pick a series first"}
                />
              </Field>
              <Field label="Language">
                <Select value={editing.language} onValueChange={(v) => setEditing({ ...editing, language: v })}>
                  <SelectTrigger className="h-10 bg-secondary border-border"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Español</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <div className="sm:col-span-2">
                <Field label={`${t("cover_url")} (Bunny CDN)`}>
                  <div className="flex gap-3 items-start">
                    <div className="w-16 h-24 rounded-lg overflow-hidden bg-secondary border border-border shrink-0 flex items-center justify-center">
                      {editing.cover ? (
                        <img src={editing.cover} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <ImageIcon className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <input className={inputCls} value={editing.cover} onChange={(e) => setEditing({ ...editing, cover: e.target.value })} placeholder="https://hearlune-public.b-cdn.net/covers/..." />
                      {editing.id ? (
                        <label className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-secondary border border-border text-xs font-medium cursor-pointer hover:border-brand-500/50 transition-colors">
                          <Upload className="w-3.5 h-3.5" />
                          {"Upload cover to Bunny"}
                          <input type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} />
                        </label>
                      ) : (
                        <p className="text-[11px] text-muted-foreground">{"Save first to enable Bunny upload"}</p>
                      )}
                      {coverUploading > 0 && <Progress value={coverUploading} className="h-1.5" />}
                    </div>
                  </div>
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Sample URL (Bunny CDN)">
                  <div className="space-y-2">
                    <input className={inputCls} value={editing.sampleUrl} onChange={(e) => setEditing({ ...editing, sampleUrl: e.target.value })} placeholder="https://hearlune-audio.b-cdn.net/samples/..." />
                    {editing.id ? (
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={handleGenerateSample}
                          disabled={sampleBusy}
                          className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-secondary border border-border text-xs font-medium hover:border-brand-500/50 transition-colors disabled:opacity-50"
                        >
                          <Scissors className="w-3.5 h-3.5" />
                          {sampleBusy ? "Cutting sample…" : "Generate sample from chapter 1"}
                        </button>
                        {editing.samplePath ? (
                          <span className="text-[11px] text-brand-400">Stored: {editing.samplePath}</span>
                        ) : (
                          <span className="text-[11px] text-muted-foreground">Generated automatically when chapter one is uploaded.</span>
                        )}
                      </div>
                    ) : (
                      <p className="text-[11px] text-muted-foreground">Save the book first, then upload chapter one to generate a sample.</p>
                    )}
                  </div>
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label={t("overview")}>
                  <textarea rows={2} className={inputCls + " h-auto py-2"} value={editing.desc_en} onChange={(e) => setEditing({ ...editing, desc_en: e.target.value })} />
                </Field>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>{t("cancel")}</Button>
            <Button onClick={save} disabled={saving} className="bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold">
              {saving ? "Saving…" : t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Chapters manager */}
      <Dialog open={!!chaptersFor} onOpenChange={(o) => !o && setChaptersFor(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {t("manage_chapters")}
              {chapterBook && <span className="text-muted-foreground text-base font-normal"> · {chapterBook.title_en}</span>}
            </DialogTitle>
          </DialogHeader>
          {chapterBook && (
            <ChapterManager book={chapterBook} addChapter={addChapter} updateChapter={updateChapter} deleteChapter={deleteChapter} playTrack={playTrack} t={t} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChapterManager({ book, addChapter, updateChapter, deleteChapter, playTrack, t }) {
  const { toast } = useToast();
  const { refresh } = useCatalog();
  const [draft, setDraft] = useState({ title_en: "", duration: "900", audioUrl: "", bunnyAsset: "" });
  const [uploading, setUploading] = useState({}); // { [chapterId]: percent }

  const add = () => {
    if (!draft.title_en) return;
    addChapter(book.id, draft);
    setDraft({ title_en: "", duration: "900", audioUrl: "", bunnyAsset: "" });
  };

  const handleAudioUpload = async (chapterId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading((u) => ({ ...u, [chapterId]: 1 }));
    try {
      await uploadFile(
        `/admin/upload/audio/${book.id}/${chapterId}`,
        file,
        (p) => setUploading((u) => ({ ...u, [chapterId]: Math.max(1, p) }))
      );
      await refresh();
    } catch (err) {
      toast({
        title: "Audio upload failed",
        description: uploadError(err),
        variant: "destructive",
      });
    }
    setUploading((u) => {
      const n = { ...u };
      delete n[chapterId];
      return n;
    });
  };

  const [bulk, setBulk] = useState({ active: false, done: 0, total: 0, current: "", pct: 0 });

  const handleBulkUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    files.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }));
    setBulk({ active: true, done: 0, total: files.length, current: "", pct: 0 });
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setBulk((s) => ({ ...s, current: f.name, done: i, pct: 0 }));
      try {
        await uploadFile(`/admin/books/${book.id}/chapters/upload`, f, (p) =>
          setBulk((s) => ({ ...s, pct: p }))
        );
      } catch (err) {
        toast({
          title: `Failed on ${f.name}`,
          description: uploadError(err),
          variant: "destructive",
        });
      }
    }
    await refresh();
    setBulk({ active: false, done: 0, total: 0, current: "", pct: 0 });
  };

  return (
    <div className="space-y-4 py-2">
      {/* Bulk upload: reads ID3 tags (title, track, duration, cover) from each MP3 */}
      <div className="rounded-xl border border-brand-500/30 bg-brand-500/5 p-4">
        <p className="text-sm font-semibold flex items-center gap-2">
          <Upload className="w-4 h-4 text-brand-400" /> Bulk upload chapters to Bunny
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Select multiple MP3 files at once. Titles, track order, duration and embedded cover are read
          automatically from each file's tags and uploaded to Bunny CDN.
        </p>
        {bulk.active ? (
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="truncate text-muted-foreground">Uploading {bulk.done + 1}/{bulk.total}: {bulk.current}</span>
              <span className="text-brand-400 font-semibold">{bulk.pct}%</span>
            </div>
            <Progress value={bulk.pct} className="h-1.5" />
          </div>
        ) : (
          <label className="mt-3 inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-brand-500 hover:bg-brand-400 text-brand-950 text-sm font-semibold cursor-pointer transition-colors">
            <Upload className="w-4 h-4" /> Select MP3 files
            <input type="file" accept="audio/*" multiple className="hidden" onChange={handleBulkUpload} />
          </label>
        )}
      </div>

      <div className="rounded-xl border border-border bg-secondary/40 p-4 space-y-3">
        <p className="text-sm font-semibold">Add chapter manually</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <input className={inputCls} placeholder="Title" value={draft.title_en} onChange={(e) => setDraft({ ...draft, title_en: e.target.value })} />
          <input className={inputCls} type="number" placeholder="Duration (s)" value={draft.duration} onChange={(e) => setDraft({ ...draft, duration: e.target.value })} />
        </div>
        <input className={inputCls} placeholder="Bunny audio path (chapters/... ) or full URL" value={draft.audioUrl} onChange={(e) => setDraft({ ...draft, audioUrl: e.target.value })} />
        <Button onClick={add} variant="outline" className="w-full border-border">
          <Plus className="w-4 h-4 mr-1.5" /> Add empty chapter
        </Button>
      </div>

      <div className="space-y-2">
        {book.chapters.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-6">{"No chapters yet."}</p>
        )}
        {book.chapters.map((ch) => (
          <div key={ch.id} className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card/50">
            <span className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold shrink-0">{ch.order}</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{ch.title_en}</p>
              <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                {formatTime(ch.duration)} · {ch.bunnyPath ? (
                  <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Bunny</span>
                ) : (ch.audioUrl?.slice(0, 32) || "no asset")}
              </p>
              {uploading[ch.id] > 0 && <Progress value={uploading[ch.id]} className="h-1 mt-1.5" />}
            </div>
            <label className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-brand-400 hover:bg-secondary transition-colors cursor-pointer" title={"Upload audio to Bunny"}>
              <Upload className="w-4 h-4" />
              <input type="file" accept="audio/*" className="hidden" onChange={(e) => handleAudioUpload(ch.id, e)} />
            </label>
            <button onClick={() => playTrack(book, ch, false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-brand-400 hover:bg-secondary transition-colors">
              <Play className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                // The upload control is 32px away and this has no undo.
                if (window.confirm(`Delete chapter ${ch.order}, "${ch.title_en}"? Its audio stays on Bunny but the chapter is gone from the book.`)) {
                  deleteChapter(book.id, ch.id);
                }
              }}
              aria-label={`Delete chapter ${ch.order}`}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-red-400 hover:bg-secondary transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
